import { type FC, useState, useEffect, useCallback } from 'react';
import { createRefetchContainer, graphql, type RelayRefetchProp } from 'react-relay/legacy.js';
import { getBuyerId, getUserType } from 'dibs-cookie-jar';
import { TRADE } from 'dibs-buyer-layout/exports/userTypeEnums.ts';
import { addLoginHandlers } from 'dibs-buyer-layout/src/utils/loginHandlers.ts';
import { trackPageview, trackUserEventHomepageView } from 'dibs-tracking';
import { mobile } from 'dibs-client-check';
import { useIntl } from 'dibs-react-intl';
import { useHomePageContext } from './HomePageContext.tsx';
import getLazyLoadStartingPoint from './helpers/getLazyLoadStartingPoint.js';
import { HpSharedAppBannerLazy } from './HpSharedAppBanner/HpSharedAppBannerLazy.tsx';
import { HpSharedCompleteEmailOptinConfirmationModalLazy } from './HpSharedCompleteEmailOptinConfirmationModal/HpSharedCompleteEmailOptinConfirmationModalLazy.tsx';
import {
    getUserSessionCountryCode,
    getUserZipCode,
} from 'dibs-regional-info/exports/regionalInfoHelpers.ts';
import { useDwelledOnPage } from 'dibs-buyer-layout/exports/dwelledOnPage.ts';
import { PAGE_TYPE } from 'dibs-constants/exports/pageTypes.ts';
import { componentModuleMap } from './componentModuleMap.tsx';
import { HpSharedBillboardAd } from './HpSharedBillboardAd/HpSharedBillboardAd.tsx';
import {
    type DeviceType,
    MOBILE,
    TABLET,
    DESKTOP,
} from 'dibs-recent-history/exports/RecentlyViewedItemsProvider.ts';
import { HpSharedRecentlyViewedItemsModule } from './HpSharedRecentlyViewedItems/HpSharedRecentlyViewedItemsModule.tsx';
import { HpRecentlySearchedItemsModuleLazy } from './HpRecentlySearchedItems/HpRecentlySearchedItemsModuleLazy.tsx';
import HpSharedNewArrivals from './HpSharedNewArrivals/HpSharedNewArrivals.tsx';
import {
    HOME_BOTTOM_BILLBOARD_PLACEMENT,
    HOME_TOP_BILLBOARD_PLACEMENT,
} from 'dibs-google-ad-manager/exports/constants.ts';
import { trackBannerAdsAbTestVariant } from 'dibs-google-ad-manager/exports/abTestHelpers.ts';

import { type HpSharedLayout_viewer$data } from './__generated__/HpSharedLayout_viewer.graphql.ts';
import HpSharedSkinnyBannerModule from './HpSharedSkinnyBannerModule/HpSharedSkinnyBannerModule.tsx';
import { HpSharedFeaturedModule } from './HpSharedFeaturedModule/HpSharedFeaturedModule.tsx';
import { HpSharedHeroModule } from './HpSharedHeroModule/HpSharedHeroModule.tsx';
import { HpSharedNewArrivalsCarouselTabs } from './HpSharedNewArrivalsCarouselTabs/HpSharedNewArrivalsCarouselTabs.tsx';
import { isRemoveShopByTestVariant } from './helpers/removeShopByTestHelpers.ts';
import { trackRemoveTopHeroBannerTest } from './helpers/removeTopHeroBannerTestHelpers.ts';
import { HpPersonalizedCollectionBanners } from './HpPersonalizedCollectionBanners/HpPersonalizedCollectionBanners.tsx';
import { VisibilityTracker } from 'dibs-visibility-tracker/exports/VisibilityTracker.tsx';
import { HpSharedFeaturedItems } from './HpSharedFeaturedItems/HpSharedFeaturedItems.tsx';
import { trackNewArrivalsCarouselTabsTest } from './helpers/newArrivalsCarouselTabsHelper.ts';
import {
    isRecentlySavedTestVariant,
    trackHpRecentlySavedTest,
} from './helpers/recentlySavedTestHelpers.ts';
import { HpSharedUserRecommendations } from './HpSharedUserRecommendations/HpSharedUserRecommendations.tsx';
import { filterFalsy } from 'dibs-ts-utils/exports/filterFalsy.ts';
import { dibsLazy } from 'dibs-lazy/exports/dibsLazy.ts';

const HpSharedRecentlyFavoritedCarouselLazy = dibsLazy(
    'HpSharedRecentlyFavoritedCarousel',
    () =>
        import(
            /* webpackChunkName: "HpSharedRecentlyFavoritedCarousel" */ './HpSharedRecentlyFavoritedCarousel/HpSharedRecentlyFavoritedCarousel.tsx'
        )
);

type HpSharedLayoutProps = {
    relay: RelayRefetchProp;
    homepageUserGroup?: string;
    viewer: HpSharedLayout_viewer$data;
};

const HpSharedLayoutComponent: FC<HpSharedLayoutProps> = ({ relay, viewer, homepageUserGroup }) => {
    const intl = useIntl();
    const [userId, setUserId] = useState<string | null>(null);
    const [userType, setUserType] = useState<string | null>(null);
    const [isClient, setIsClient] = useState<boolean>(false);
    const [fetchFavorites, setFetchFavorites] = useState(false);

    const {
        isMobile,
        isTablet,
        isLoggedInUser,
        isTopDisplayAdEnabled,
        isBottomDisplayAdEnabled,
        isEvergreenCollectionsTestVariant,
        isNewArrivalsCarouselTabsTestVariant,
        isBot,
    } = useHomePageContext();

    let deviceType: DeviceType = DESKTOP;
    if (isMobile) {
        deviceType = MOBILE;
    } else if (isTablet) {
        deviceType = TABLET;
    }

    const user = viewer?.user || null;

    const hpUserGroupOrUserType = homepageUserGroup || userType || '';

    const doRefetch = useCallback(
        (additionalVariables = {}) => {
            relay.refetch(
                fragmentVariables => ({
                    ...fragmentVariables,
                    fetchInitialHomepage: false,
                    isClient: true,
                    isMobile,
                    hasUserId: !!userId,
                    userId: userId || '',
                    userIds: userId ? [userId] : [],
                    userType: hpUserGroupOrUserType,
                    zipCode: getUserZipCode(),
                    isTrade: hpUserGroupOrUserType === TRADE,
                    userCountryCode: getUserSessionCountryCode(),
                    isRemoveShopByTestVariant: isRemoveShopByTestVariant(),
                    ...additionalVariables,
                }),
                { fetchInitialHomepage: true },
                () => {
                    setIsClient(true);

                    // ensure that we are done with refetch before considering to allow favorites fetch
                    if (userId) {
                        setFetchFavorites(true);
                    }
                }
            );
        },
        [userId, hpUserGroupOrUserType, relay, isMobile]
    );

    const setUserData = useCallback(() => {
        const cookieUserId = getBuyerId(document.cookie);

        if (cookieUserId && !userId) {
            // order is important to prevent unnecessary refetch, read more: https://github.com/1stdibs/ferrum/pull/13601#pullrequestreview-818832289
            setUserType(getUserType(document.cookie));
            setUserId(cookieUserId);
        }
    }, [userId]);

    useEffect(() => {
        trackBannerAdsAbTestVariant();
    }, []);

    useEffect(() => {
        trackRemoveTopHeroBannerTest();
    }, []);

    useEffect(() => {
        trackPageview({ additional: { opt_pageURL: '/' } });
    }, []);

    useEffect(() => {
        trackUserEventHomepageView(relay.environment);
    }, [relay.environment]);

    useEffect(() => {
        if (!isLoggedInUser && !isClient) {
            doRefetch();
        }
    }, [doRefetch, isLoggedInUser, isClient]);

    useEffect(() => {
        if (userId && !user) {
            doRefetch();
        }
    }, [doRefetch, userId, user]);

    useEffect(() => {
        setUserData();
        addLoginHandlers((data: { user: { isVerifiedTrade?: boolean | null } }) => {
            if (data?.user?.isVerifiedTrade) {
                // keep this to make sure a trade member
                // logging in on the hp is "redirected"
                // to the trade homepage
                window.location.reload();
                return;
            }
            setUserData();
        });
    }, [setUserData, intl.locale]);

    useDwelledOnPage(PAGE_TYPE.HOME);

    const { homepage } = viewer;

    const isUser = isLoggedInUser || !!userId; // isLoggedInUser comes at load; userId might come later from refetch

    const { personalization, personalizationCollections, modules } = homepage || {};

    const promoPrimaryModule = (modules || []).find(
        mod => mod && mod.type === 'promo' && mod.index === 0
    );
    const promoPrimaryItemsCount = (promoPrimaryModule?.messages?.items || []).length;
    const startLazyLoadAt = getLazyLoadStartingPoint({ isMobile, isTablet });
    const hasLoggedInWithApp = user?.hasLoggedInWithApp;

    let editorialIndex = 1;

    const mods = (modules || []).map((mod, index) => {
        if (!mod) {
            return null;
        }
        const type = mod.type;

        if (!type) {
            return null;
        }

        const component = componentModuleMap[type];
        if (component) {
            return component({
                componentModule: mod,
                key: `${type}-${index}`,
                startingPromoIndex: type === 'promo' && mod.index ? promoPrimaryItemsCount : 0,
                viewer,
                isMobile,
                useLazyLoadImages: index >= startLazyLoadAt,
                moduleIndex: index,
                totalModules: modules?.length || 0,
                user,
                isClient,
                isEvergreenCollectionsTestVariant: !!isEvergreenCollectionsTestVariant,
                editorialModuleOrder: type === 'editorial' ? editorialIndex++ : 0,
            });
        }
        return null;
    });

    const getModuleIndex = ({
        moduleKey,
        direction = 'forward',
    }: {
        moduleKey: string;
        direction?: 'forward' | 'backward';
    }): number => {
        const moduleIndex =
            direction === 'forward'
                ? mods.findIndex(mod => (mod?.key || '').toString().includes(moduleKey))
                : mods.findLastIndex(mod => (mod?.key || '').toString().includes(moduleKey));

        if (moduleIndex === -1) {
            return mods.length - 1;
        }

        return moduleIndex;
    };

    const isOneEditorialModule =
        getModuleIndex({ moduleKey: 'editorial' }) ===
        getModuleIndex({ moduleKey: 'editorial', direction: 'backward' });

    /**
     * non cms controlled modules insertion
     */
    mods.splice(
        0,
        0,
        <HpSharedRecentlyViewedItemsModule
            key="hp-recentlyViewed"
            moduleIndex={1}
            totalModules={modules?.length || 0}
            deviceType={deviceType}
        />
    );

    const newArrivalsCarouselTabsTestIndex = getModuleIndex({ moduleKey: 'editorial' }) + 1;
    const newArrivalsCarouselTabsTestTracker = (
        <VisibilityTracker
            key="hp-new-arrivals-ab-test-tracker"
            onVisibilityChange={({ isVisible }) => {
                if (isVisible) {
                    trackNewArrivalsCarouselTabsTest();
                }
            }}
        />
    );
    if (isUser) {
        mods.splice(
            newArrivalsCarouselTabsTestIndex,
            0,
            newArrivalsCarouselTabsTestTracker,
            isNewArrivalsCarouselTabsTestVariant ? (
                <HpSharedNewArrivalsCarouselTabs
                    key="hp-new-arrivals-carousel-tabs"
                    viewer={viewer}
                    user={user || null}
                    personalizationCollections={
                        personalizationCollections?.filter(filterFalsy) || null
                    }
                />
            ) : (
                <HpSharedNewArrivals
                    key="hp-new-arrivals"
                    viewer={viewer}
                    user={user || null}
                    personalization={personalization || null}
                    fetchFavorites={fetchFavorites}
                />
            )
        );
    }

    mods.splice(
        isUser
            ? getModuleIndex({ moduleKey: 'hp-new-arrivals' }) + 1
            : getModuleIndex({ moduleKey: 'editorial' }) + 1,
        0,
        <HpSharedHeroModule
            key="hp-editors-picks"
            viewer={viewer}
            user={user}
            isMobile={isMobile}
            isClient={isClient}
        />
    );

    mods.splice(
        getModuleIndex({ moduleKey: 'hp-editors-picks' }),
        0,
        <HpRecentlySearchedItemsModuleLazy key="hp-recentlySearched" />
    );

    mods.splice(
        getModuleIndex({ moduleKey: 'locations' }) + 1,
        0,
        <HpSharedFeaturedModule
            key="featured"
            viewer={isClient ? viewer : null}
            user={user}
            isClient={isClient}
        />
    );

    mods.splice(
        getModuleIndex({ moduleKey: 'editorial' }) + 1,
        0,
        <HpSharedUserRecommendations key="hp-user-recommendations" user={user} />,
        <HpPersonalizedCollectionBanners
            key="hp-personalized-catalogues-banner-0"
            bannerIndex={0}
        />
    );
    mods.splice(
        getModuleIndex({ moduleKey: 'hp-editors-picks' }) + 1,
        0,
        <HpPersonalizedCollectionBanners
            key="hp-personalized-catalogues-banner-1"
            bannerIndex={1}
        />
    );
    mods.splice(
        getModuleIndex({ moduleKey: 'locations' }) + 1,
        0,
        <HpPersonalizedCollectionBanners
            key="hp-personalized-catalogues-banner-2"
            bannerIndex={2}
        />
    );
    mods.splice(
        getModuleIndex({ moduleKey: 'featured' }) + 1,
        0,
        <HpPersonalizedCollectionBanners
            key="hp-personalized-catalogues-banner-3"
            bannerIndex={3}
        />
    );

    const recentlySavedInsertIndex = getModuleIndex({ moduleKey: 'featured' }) + 2;
    const recentlySavedAbTestTracker = (
        <VisibilityTracker
            key="hp-recently-saved-ab-test-tracker"
            onVisibilityChange={({ isVisible }) => {
                if (isVisible) {
                    trackHpRecentlySavedTest();
                }
            }}
        />
    );
    if (isClient) {
        if (isRecentlySavedTestVariant()) {
            mods.splice(
                recentlySavedInsertIndex,
                0,
                recentlySavedAbTestTracker,
                <HpSharedRecentlyFavoritedCarouselLazy
                    key="hp-recently-saved"
                    userId={userId || ''}
                    isTrade={hpUserGroupOrUserType === TRADE}
                    priceBookName={''}
                />
            );
        } else {
            mods.splice(recentlySavedInsertIndex, 0, recentlySavedAbTestTracker);
        }
    }

    if (isOneEditorialModule) {
        mods.splice(
            getModuleIndex({ moduleKey: 'shopBy' }),
            0,
            <HpSharedSkinnyBannerModule key="skinnyBanner" />
        );
    } else {
        mods.splice(
            getModuleIndex({ moduleKey: 'editorial', direction: 'backward' }),
            0,
            <HpSharedSkinnyBannerModule key="skinnyBanner" />
        );
    }

    if (isTopDisplayAdEnabled) {
        mods.splice(
            1, // insert after the recently viewed items module
            0,
            <HpSharedBillboardAd
                key="billboardAd-home-top"
                placement={HOME_TOP_BILLBOARD_PLACEMENT}
            />
        );
    }

    if (isBottomDisplayAdEnabled) {
        mods.splice(
            getModuleIndex({ moduleKey: 'locations' }) + 1,
            0,
            <HpSharedBillboardAd
                key="billboardAd-home-bottom"
                placement={HOME_BOTTOM_BILLBOARD_PLACEMENT}
            />
        );
    }

    if (!isBot) {
        mods.splice(
            getModuleIndex({ moduleKey: 'locations' }) + 1,
            0,
            <HpSharedFeaturedItems
                key={`featured-items${userId ? `-${userId}` : ''}`}
                userId={userId}
                isTrade={hpUserGroupOrUserType === TRADE}
            />
        );
    }

    return (
        <>
            {mods}
            {isClient && <HpSharedCompleteEmailOptinConfirmationModalLazy />}
            {isClient &&
                (mobile.iPhone() || mobile.iPad()) &&
                !(isLoggedInUser && hasLoggedInWithApp) && <HpSharedAppBannerLazy user={user} />}
        </>
    );
};

export const HpSharedLayout = createRefetchContainer(
    HpSharedLayoutComponent,
    {
        viewer: graphql`
            fragment HpSharedLayout_viewer on Viewer {
                homepage(rootId: $homepageId, abTestList: $abTestList) {
                    modules {
                        ... @skip(if: $isRemoveTopHeroBannerTestVariant) {
                            # don't skip this module on refetch, otherwise the dibs-carousel within
                            # won't get any items
                            ... on TopHeroBannerModule {
                                type
                                ...HpSharedTopHeroBannerModule_componentModule
                            }
                        }
                        ... on CollectionsModule @include(if: $fetchInitialHomepage) {
                            type
                            ...HpSharedCollectionsModule_componentModule
                        }
                        ... on PromoModule {
                            index
                            type
                            messages(userType: $userType) {
                                items {
                                    __typename
                                }
                            }
                            ...HpSharedPromoModule_componentModule
                        }
                        ... on EditorialModule @include(if: $fetchInitialHomepage) {
                            type
                            ...HpSharedEditorialModule_componentModule
                        }
                        ... @skip(if: $isRemoveShopByTestVariant) {
                            ... on ShopByModule @include(if: $isClient) {
                                type
                                ...HpSharedShopByModule_componentModule
                            }
                        }
                        ... on LocationsModule {
                            type
                        }
                        ... on BottomBannerModule {
                            type
                            # visible used in componentModuleMap to check if the module is visible
                            # eslint-disable-next-line relay/unused-fields
                            visible
                            ...HpSharedFeaturedBannerModule_componentModule
                        }
                        ... on VideoModule @include(if: $isClient) {
                            type
                            ...HpSharedVideoComponent_componentModule
                        }
                    }
                    personalization(userId: $userId, first: 3, numItems: 3)
                        @include(if: $hasUserId) {
                        ...HpSharedNewArrivals_personalization
                            @skip(if: $isNewArrivalsCarouselTabsTestVariant)
                    }
                    personalizationCollections(userId: $userId, numItems: 10, pageSize: 3)
                        @include(if: $hasUserId) {
                        ...HpSharedNewArrivalsCarouselTabs_personalizationCollections
                            @include(if: $isNewArrivalsCarouselTabsTestVariant)
                    }
                }
                ...HpSharedHeroModule_viewer
                ... @include(if: $isEvergreenCollectionsTestVariant) {
                    ...HpSharedTrendingNowCarouselTabs_viewer @include(if: $isClient)
                }
                ... @skip(if: $isEvergreenCollectionsTestVariant) {
                    ...HpSharedLocationsModule_viewer @include(if: $isClient)
                }
                ...HpSharedNewArrivals_viewer @skip(if: $isNewArrivalsCarouselTabsTestVariant)
                ...HpSharedNewArrivalsTabContent_viewer
                    @include(if: $isNewArrivalsCarouselTabsTestVariant)
                ...HpSharedFeaturedModule_viewer @include(if: $isClient)
                ... @skip(if: $isRemoveTopHeroBannerTestVariant) {
                    ...HpSharedTopHeroBannerModule_viewer
                }
                ...HpSharedVideoComponent_viewer @include(if: $isClient)
                user(userId: $userId) @include(if: $hasUserId) {
                    hasLoggedInWithApp: hasLoggedInWithDeviceType(deviceType: IOS_APP)
                    ...HpSharedHeroModule_user
                    ...HpSharedTrendingNowCarouselTabs_user
                        @include(if: $isEvergreenCollectionsTestVariant)
                    ...HpSharedLocationsModule_user @skip(if: $isEvergreenCollectionsTestVariant)
                    ...HpSharedNewArrivals_user @skip(if: $isNewArrivalsCarouselTabsTestVariant)
                    ...HpSharedNewArrivalsTabContent_user
                        @include(if: $isNewArrivalsCarouselTabsTestVariant)
                    ...HpSharedVideoComponent_user @include(if: $isClient)
                    ...HpSharedFeaturedModule_user
                    ...HpSharedAppBanner_user
                    ... @skip(if: $isRemoveTopHeroBannerTestVariant) {
                        ...HpSharedTopHeroBannerModule_user
                    }
                    ...HpSharedUserRecommendations_user
                }
            }
        `,
    },
    graphql`
        query HpSharedLayoutRefetchQuery(
            $hasUserId: Boolean!
            $userId: String!
            $homepageId: String!
            $isMobile: Boolean!
            $fetchInitialHomepage: Boolean!
            $userType: String!
            $abTestList: [String] = []
            $userCountryCode: String!
            $isTrade: Boolean!
            $priceBookName: String!
            $isClient: Boolean!
            $isRemoveShopByTestVariant: Boolean!
            $isEvergreenCollectionsTestVariant: Boolean!
            $isRemoveTopHeroBannerTestVariant: Boolean!
            $isNewArrivalsCarouselTabsTestVariant: Boolean!
        ) {
            viewer {
                ...HpSharedLayout_viewer
            }
        }
    `
);
